import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { authApi } from "../api/axios";

const validations = [
  { regex: /.{8,}/, label: "At least 8 Characters" },
  { regex: /[A-Z]/, label: "At least 1 UpperCase Letter" },
  { regex: /[a-z]/, label: "At least 1 LowerCase Letter" },
  { regex: /\d/, label: "At least 1 Digit" },
  { regex: /[!@#$%^&*(),.?":{}|<>]/, label: "At least 1 Special Character" },
];

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const code = searchParams.get("code");
  const firstTimeLogin = searchParams.get("firstLogin") === "true";
  const firstName = searchParams.get("firstName");

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showValidation, setShowValidation] = useState(false);

  const pwdRef = useRef(null);
  const confirmRef = useRef(null);

  const getStrength = () => {
    const passed = validations.filter((v) => v.regex.test(password)).length;
    if (passed <= 2) return "Very Weak";
    if (passed === 3) return "Weak";
    if (passed === 4) return "Good";
    if (passed === 5) return "Strong";
  };

  const validate = () => {
    if (!password.trim() && !confirmPassword.trim()) {
      toast.error("Please enter password and confirm password");
      if (pwdRef.current) pwdRef.current.focus();
      return false;
    }
    if (!password.trim()) {
      toast.error("Please enter password");
      if (pwdRef.current) pwdRef.current.focus();
      return false;
    }
    if (!confirmPassword.trim()) {
      toast.error("Please enter confirm password");
      if (confirmRef.current) confirmRef.current.focus();
      return false;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      if (confirmRef.current) confirmRef.current.focus();
      return false;
    }
    const passed = validations.filter((v) => v.regex.test(password)).length;
    if (passed < 4) {
      toast.error("Password is too weak. Please follow the password rules.");
      if (pwdRef.current) pwdRef.current.focus();
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { data } = await authApi.post("/account/resetPassword", {
        userId,
        code,
        isFirstLogin: firstTimeLogin,
        firstName,
        password,
      });

      if (data?.success) {
        toast.success(
          data.message || "Password reset successfully! Redirecting...",
        );
        setTimeout(() => navigate("/"), 1800);
      } else {
        toast.error(data?.message || "Reset failed");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "An error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold mb-1 mt-6">Reset Password</h2>
        <p className="text-gray-500 text-sm">Enter your new password</p>
      </div>

      <form
        noValidate
        onSubmit={handleSubmit}
        className="space-y-4 w-full max-w-sm mx-auto"
      >
        <div className="relative">
          <input
            ref={pwdRef}
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onFocus={() => setShowValidation(true)}
            onBlur={() => setShowValidation(false)}
            onChange={(e) => setPassword(e.target.value)}
            className="block w-full border border-gray-300 rounded-lg shadow-sm pl-3 pr-10 py-3 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-transparent bg-white"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-primaryColor text-sm"
          >
            {showPassword ? "Hide" : "Show"}
          </button>

          {showValidation && password.length > 0 && (
            <div className="absolute left-0 mt-2 w-full bg-white border rounded-lg shadow-lg p-3 text-sm z-10">
              <p className="font-semibold">
                Password Strength:{" "}
                <span
                  className={
                    getStrength() === "Strong"
                      ? "text-green-600"
                      : getStrength() === "Good"
                        ? "text-primaryColor"
                        : getStrength() === "Weak"
                          ? "text-yellow-600"
                          : "text-red-600"
                  }
                >
                  {getStrength()}
                </span>
              </p>
              <p className="mt-1 font-medium">Password Must Include:</p>
              <ul className="mt-1 space-y-1">
                {validations.map((v, i) => (
                  <li key={i} className="flex items-center">
                    <span
                      className={
                        v.regex.test(password)
                          ? "text-green-500 mr-2"
                          : "text-red-500 mr-2"
                      }
                    >
                      {v.regex.test(password) ? "✔" : "✖"}
                    </span>
                    {v.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="relative">
          <input
            ref={confirmRef}
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="block w-full border border-gray-300 rounded-lg shadow-sm pl-3 pr-10 py-3 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-transparent bg-white"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-primaryColor text-sm"
          >
            {showConfirmPassword ? "Hide" : "Show"}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center bg-primaryColor text-white py-3 rounded-md text-sm hover:bg-secondaryColor transition-colors duration-300 disabled:cursor-not-allowed"
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>
    </>
  );
}

export default ResetPassword;
